import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import get from 'lodash/get';

import { useAccountGet } from '@sbv-clients/data-access-api';
import { useQueryParamStore } from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  useFiltersStore,
  useParamStore,
  useScheduleStatusInitStore,
} from '@sbv-clients/shared-state';
import {
  Box,
  OverlayLoader,
  Select,
  SelectChangeEvent,
  SelectOption,
  toReadableLabel,
  Typography,
  useTheme,
} from '@sbv-clients/ui-shared';
import { FunctionComponent } from '@sbv-clients/util-misc';

import { useLoginToNewProfile } from '../../hooks/use-login-to-new-profile';

interface DBSwitcherProps {
  isRoot?: boolean;
}

type ProfileOption = SelectOption<string>;

export const DBSwitcher: FunctionComponent<DBSwitcherProps> = ({
  isRoot = false,
}) => {
  const paramStore = useParamStore();
  const scheduleStatusInitStore = useScheduleStatusInitStore();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { data: accountData, isLoading: isLoadingAccount } = useAccountGet();
  const { mutateAsync: loginToNewProfile } = useLoginToNewProfile();
  const { onAuthSuccess } = useAuth();

  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const profileOptions = useMemo<ProfileOption[]>(() => {
    return (accountData?.profiles ?? []).map((profile) => ({
      id: profile.id as string,
      value: profile.id as string,
      label: toReadableLabel(
        profile?.databaseName || profile?.facilityName || ''
      ),
    }));
  }, [accountData?.profiles]);

  useEffect(() => {
    if (accountData?.tokenProfileId) {
      setSelectedProfileId(accountData.tokenProfileId);
    }
  }, [accountData?.tokenProfileId]);

  const isEmployeePath = useCallback((pathname: string) => {
    return pathname === '/employees' || pathname.startsWith('/employees/');
  }, []);

  const handleSwitch = useCallback(
    async (nextProfileId: string) => {
      const previousProfileId = selectedProfileId;
      try {
        setSelectedProfileId(nextProfileId);
        setIsLoading(true);
        const { data } = await loginToNewProfile(nextProfileId);
        queryClient.clear();
        paramStore.clearAllPages();
        scheduleStatusInitStore.reset();
        const token = get(data, 'token', '') as string;
        const refreshToken = get(data, 'refreshToken', '') as string;
        onAuthSuccess(token, refreshToken);
        const { pathname } = window.location;
        window.location.replace(pathname.split('/').filter(Boolean)[0] || '/');

        useQueryParamStore.getState().resetAll();
        useFiltersStore.getState().resetAll();
      } catch {
        setSelectedProfileId(previousProfileId);
      } finally {
        setIsLoading(false);
      }
    },
    [
      loginToNewProfile,
      onAuthSuccess,
      paramStore,
      queryClient,
      scheduleStatusInitStore,
      selectedProfileId,
    ]
  );

  const handleChange = useCallback(
    async (event: SelectChangeEvent<unknown>) => {
      const nextProfileId = event.target.value as string;

      if (!nextProfileId || nextProfileId === accountData?.tokenProfileId) {
        return;
      }

      setIsLoading(true);
      await handleSwitch(nextProfileId);
    },
    [accountData?.tokenProfileId, handleSwitch]
  );

  if (!profileOptions.length) return null;

  const isSingleProfile = profileOptions.length === 1;
  const singleProfile = isSingleProfile ? profileOptions[0] : null;

  const renderStyledText = (text: React.ReactNode) => (
    <Typography
      sx={{ marginTop: '-3px' }}
      fontSize="16px"
      fontWeight={isRoot ? 500 : undefined}
      color={isRoot ? 'primary.main' : theme.text?.secondary}
    >
      {text}
    </Typography>
  );

  return (
    <Box>
      {isLoading && <OverlayLoader />}
      {isSingleProfile && singleProfile ? (
        renderStyledText(singleProfile.label)
      ) : (
        <Select
          firstOptionAsDefault={
            !isLoadingAccount && !accountData?.tokenProfileId
          }
          sx={{
            backgroundColor: theme.background.lightGrey,
            boxShadow: 'none',
            width: { xs: '180px', sm: 'auto' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            },
            '&:hover': {
              backgroundColor: theme.background.lightGrey3,
            },
          }}
          labelId="DB-select-label"
          value={selectedProfileId}
          onChange={(e) => handleChange(e)}
          disabled={isLoading}
          options={profileOptions}
          renderValue={(selected) => {
            const option = profileOptions.find((o) => o.value === selected);
            if (!option) return '';
            return (
              <Typography
                sx={{
                  marginTop: '-3px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
                fontSize="16px"
                fontWeight={isRoot ? 500 : undefined}
                color={isRoot ? 'primary.main' : theme.text?.secondary}
              >
                {option.label}
              </Typography>
            );
          }}
        />
      )}
    </Box>
  );
};
